import React, { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { Box, Button, DatePicker, Flex, Heading, Notification, Table, Text, TextField } from "@telia/teddy";
import { API_URL_SUPERADMIN_DOCUMENT, API_URL_SUPERADMIN_DOCUMENTS } from "../../api";
import { formatDateForInput } from "./documentDateUtils";

type DocumentType = "PROVISJONSBULLETIN" | "PRESENTASJON" | "SKJEMA" | "RUTINER";
type Chain = "ELKJOP" | "POWER" | "D2D" | "EXTERNAL_RETAIL" | "RETAIL_AND_D2D" | "TM_INTERNAL" | "TM_EXTERNAL";

type Document = {
    id: string;
    title: string;
    ingress: string;
    fileName: string;
    documentType: DocumentType;
    chain: Chain | null;
    startDate: string;
    endDate: string | null;
    uploadedAt: string;
    uploadedBy: string;
    updatedAt: string;
};

type FormState = {
    chain: Chain | "";
    documentType: DocumentType | "";
    endDate: string;
    ingress: string;
    startDate: string;
    title: string;
};

const emptyForm: FormState = {
    title: "",
    ingress: "",
    chain: "EXTERNAL_RETAIL",
    documentType: "",
    startDate: "",
    endDate: "",
};

const CHAIN_LABELS: Record<Chain, string> = {
    ELKJOP: "Elkjøp",
    POWER: "Power",
    D2D: "D2D",
    EXTERNAL_RETAIL: "Ekstern retail",
    RETAIL_AND_D2D: "Ekstern retail",
    TM_INTERNAL: "TM intern",
    TM_EXTERNAL: "TM ekstern",
};

const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
    PROVISJONSBULLETIN: "Provisjonsbulletin",
    PRESENTASJON: "Presentasjoner",
    SKJEMA: "Skjema",
    RUTINER: "Rutiner",
};

const formatDate = (iso: string | null): string => {
    if (!iso) return "–";
    return new Date(iso).toLocaleDateString("nb-NO");
};

// Reads the response body as text first (works even when it isn't valid JSON, e.g. an HTML
// error page from a proxy/WAF in front of the app) and logs enough context to diagnose failures
// that only happen in AT/prod and not locally.
const parseAndLogErrorResponse = async (res: Response, context: string, extra?: Record<string, unknown>) => {
    const bodyText = await res.text().catch(() => "");
    let parsed: { error?: string } = {};
    try {
        parsed = bodyText ? JSON.parse(bodyText) : {};
    } catch {
        // response body was not JSON, e.g. an HTML error page from ALB/CloudFront/WAF
    }

    const details = {
        context,
        status: res.status,
        statusText: res.statusText,
        contentType: res.headers.get("content-type"),
        bodySnippet: bodyText.slice(0, 500),
        ...extra,
    };

    Sentry.captureMessage(`DocumentUpload: ${context}`, (scope) => {
        scope.setLevel("error");
        scope.setTag("errorType", "document-upload");
        Object.entries(details).forEach(([key, value]) => scope.setExtra(key, value));
        return scope;
    });

    return parsed.error ?? `Noe gikk galt (${res.status} ${res.statusText})`;
};

// Handles the case where `fetch` itself rejects (network error, CORS, blocked by browser/extension)
// as opposed to the server responding with a non-2xx status, which is handled separately above.
const logNetworkError = (context: string, err: unknown) => {
    Sentry.captureException(err, (scope) => {
        scope.setTag("errorType", "document-upload-network");
        scope.setExtra("context", context);
        return scope;
    });
};

const DocumentUpload: React.FC = () => {
    const [documents, setDocuments] = useState<Document[]>([]);
    const [form, setForm] = useState(emptyForm);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [showExpired, setShowExpired] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const now = new Date();
    const visibleDocuments = showExpired
        ? documents
        : documents.filter((d) => !d.endDate || new Date(d.endDate) >= now);

    const fetchDocuments = () => {
        setLoading(true);
        fetch(API_URL_SUPERADMIN_DOCUMENTS)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`Serverfeil: ${res.status} ${res.statusText}`);
                }
                return res.json() as Promise<Document[]>;
            })
            .then((data) => {
                if (!Array.isArray(data)) {
                    throw new Error("Uventet svar fra server");
                }
                setDocuments(data);
                setLoading(false);
            })
            .catch((err: Error) => {
                setError(err.message || "Kunne ikke laste dokumenter");
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchDocuments();
    }, []);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!editingId && !selectedFile) {
            setError("Du må velge en PDF-fil");
            return;
        }
        if (!form.documentType) {
            setError("Du må velge dokumenttype");
            return;
        }

        if (editingId) {
            // Metadata-only update
            const payload: Record<string, unknown> = {
                title: form.title.trim(),
                ingress: form.ingress.trim(),
                documentType: form.documentType,
                chain: form.chain || null,
                startDate: form.startDate,
                endDate: form.endDate || null,
            };
            fetch(API_URL_SUPERADMIN_DOCUMENT(editingId), {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            })
                .then(async (res) => {
                    if (!res.ok) {
                        const message = await parseAndLogErrorResponse(res, "updateDocumentHandler", {
                            documentId: editingId,
                        });
                        throw Object.assign(new Error(message), { alreadyLogged: true });
                    }
                    setForm(emptyForm);
                    setEditingId(null);
                    fetchDocuments();
                })
                .catch((err: Error & { alreadyLogged?: boolean }) => {
                    if (!err.alreadyLogged) logNetworkError("updateDocumentHandler", err);
                    setError(err.message);
                });
        } else {
            // New upload — multipart/form-data
            const formData = new FormData();
            formData.append("file", selectedFile as File);
            formData.append("title", form.title.trim());
            formData.append("ingress", form.ingress.trim());
            formData.append("documentType", form.documentType);
            if (form.chain) {
                formData.append("chain", form.chain);
            }
            formData.append("startDate", form.startDate);
            if (form.endDate) {
                formData.append("endDate", form.endDate);
            }

            fetch(API_URL_SUPERADMIN_DOCUMENTS, { method: "POST", body: formData })
                .then(async (res) => {
                    if (!res.ok) {
                        const message = await parseAndLogErrorResponse(res, "createDocumentHandler", {
                            fileName: selectedFile?.name,
                            fileSizeBytes: selectedFile?.size,
                            documentType: form.documentType,
                        });
                        throw Object.assign(new Error(message), { alreadyLogged: true });
                    }
                    setForm(emptyForm);
                    setSelectedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                    fetchDocuments();
                })
                .catch((err: Error & { alreadyLogged?: boolean }) => {
                    if (!err.alreadyLogged) logNetworkError("createDocumentHandler", err);
                    setError(err.message);
                });
        }
    };

    const handleEdit = (doc: Document) => {
        setEditingId(doc.id);
        setForm({
            title: doc.title,
            ingress: doc.ingress,
            documentType: doc.documentType,
            chain: !doc.chain || doc.chain === "RETAIL_AND_D2D" ? "EXTERNAL_RETAIL" : doc.chain,
            startDate: doc.startDate.slice(0, 10),
            endDate: doc.endDate ? doc.endDate.slice(0, 10) : "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleDelete = (id: string) => {
        if (!window.confirm("Er du sikker på at du vil slette dette dokumentet? PDF-filen fjernes fra lagring."))
            return;
        fetch(API_URL_SUPERADMIN_DOCUMENT(id), { method: "DELETE" })
            .then((res) => {
                if (!res.ok) throw new Error();
                fetchDocuments();
            })
            .catch(() => setError("Kunne ikke slette dokument"));
    };

    const handleCancel = () => {
        setEditingId(null);
        setForm(emptyForm);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setError(null);
    };

    return (
        <div className="retailer-dashboard-wrapper">
            <Heading as="h1" variant="title-300" mb="400">
                Dokumentopplasting
            </Heading>

            <Box mb="600">
                <Heading as="h2" variant="title-200" mb="300">
                    {editingId ? "Rediger dokument" : "Last opp nytt dokument"}
                </Heading>

                {error && (
                    <Notification type="tile" variant="error" mb="300">
                        <Notification.Icon />
                        <Notification.Text>{error}</Notification.Text>
                    </Notification>
                )}

                <form onSubmit={handleSubmit}>
                    <Flex direction="column" gap="300" style={{ maxWidth: "560px" }}>
                        <TextField>
                            <TextField.Label>Overskrift</TextField.Label>
                            <TextField.Input
                                value={form.title}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setForm((f) => ({ ...f, title: e.target.value }))
                                }
                            />
                        </TextField>

                        <TextField>
                            <TextField.Label>Ingress</TextField.Label>
                            <TextField.Input
                                value={form.ingress}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setForm((f) => ({ ...f, ingress: e.target.value }))
                                }
                            />
                        </TextField>

                        <Box>
                            <label
                                htmlFor="document-type-select"
                                style={{ display: "block", marginBottom: "4px", fontWeight: 500 }}
                            >
                                Dokumenttype
                            </label>
                            <select
                                id="document-type-select"
                                value={form.documentType}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, documentType: e.target.value as DocumentType | "" }))
                                }
                                style={{ width: "100%", padding: "8px", fontSize: "inherit" }}
                            >
                                <option value="">Velg type</option>
                                <option value="PROVISJONSBULLETIN">Provisjonsbulletin</option>
                                <option value="PRESENTASJON">Presentasjoner</option>
                                <option value="SKJEMA">Skjema</option>
                                <option value="RUTINER">Rutiner</option>
                            </select>
                        </Box>

                        <Box>
                            <label
                                htmlFor="chain-select"
                                style={{ display: "block", marginBottom: "4px", fontWeight: 500 }}
                            >
                                Kjede
                            </label>
                            <select
                                id="chain-select"
                                value={form.chain}
                                onChange={(e) => setForm((f) => ({ ...f, chain: e.target.value as Chain | "" }))}
                                style={{ width: "100%", padding: "8px", fontSize: "inherit" }}
                            >
                                <option value="EXTERNAL_RETAIL">Alle</option>
                                <option value="ELKJOP">Elkjøp</option>
                                <option value="POWER">Power</option>
                                <option value="D2D">D2D</option>
                                <option value="TM_INTERNAL">TM intern</option>
                                <option value="TM_EXTERNAL">TM ekstern</option>
                            </select>
                        </Box>

                        <Flex gap="300">
                            <Box style={{ flex: 1 }}>
                                <DatePicker
                                    label="Startdato for visning (fra kl. 08:00)"
                                    value={form.startDate}
                                    onSelectedDate={(date) =>
                                        setForm((f) => ({
                                            ...f,
                                            startDate: date ? formatDateForInput(date) : "",
                                        }))
                                    }
                                />
                            </Box>

                            <Box style={{ flex: 1 }}>
                                <DatePicker
                                    label="Sluttdato for visning (valgfri)"
                                    value={form.endDate}
                                    min={form.startDate}
                                    onSelectedDate={(date) =>
                                        setForm((f) => ({
                                            ...f,
                                            endDate: date ? formatDateForInput(date) : "",
                                        }))
                                    }
                                />
                            </Box>
                        </Flex>

                        {!editingId && (
                            <Box>
                                <label
                                    htmlFor="pdf-file-input"
                                    style={{ display: "block", marginBottom: "4px", fontWeight: 500 }}
                                >
                                    PDF-fil (maks 50 MB)
                                </label>
                                <input
                                    id="pdf-file-input"
                                    ref={fileInputRef}
                                    type="file"
                                    accept="application/pdf"
                                    onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                                    style={{ display: "block", width: "100%" }}
                                />
                                {selectedFile && (
                                    <Text style={{ marginTop: "4px", fontSize: "0.875rem" }}>
                                        Valgt: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                                    </Text>
                                )}
                            </Box>
                        )}

                        <Flex gap="200">
                            <Button type="submit" variant="primary">
                                {editingId ? "Lagre endringer" : "Last opp"}
                            </Button>
                            {editingId && (
                                <Button type="button" variant="secondary" onClick={handleCancel}>
                                    Avbryt
                                </Button>
                            )}
                        </Flex>
                    </Flex>
                </form>
            </Box>

            <Heading as="h2" variant="title-200" mb="300">
                Alle dokumenter
            </Heading>

            <Flex align="center" gap="200" mb="300">
                <input
                    id="show-expired-docs"
                    type="checkbox"
                    checked={showExpired}
                    onChange={(e) => setShowExpired(e.target.checked)}
                />
                <label htmlFor="show-expired-docs" style={{ cursor: "pointer" }}>
                    Vis utgåtte
                </label>
            </Flex>

            {loading ? (
                <Text>Laster...</Text>
            ) : (
                <Table style={{ width: "100%", tableLayout: "auto" }}>
                    <Table.Header>
                        <Table.Row>
                            <Table.Head style={{ whiteSpace: "nowrap" }}>Overskrift</Table.Head>
                            <Table.Head style={{ whiteSpace: "nowrap" }}>Ingress</Table.Head>
                            <Table.Head style={{ whiteSpace: "nowrap" }}>Type</Table.Head>
                            <Table.Head style={{ whiteSpace: "nowrap" }}>Kjede</Table.Head>
                            <Table.Head style={{ whiteSpace: "nowrap" }}>Fil</Table.Head>
                            <Table.Head style={{ whiteSpace: "nowrap" }}>Startdato</Table.Head>
                            <Table.Head style={{ whiteSpace: "nowrap" }}>Sluttdato</Table.Head>
                            <Table.Head style={{ whiteSpace: "nowrap" }}>Lastet opp av</Table.Head>
                            <Table.Head style={{ whiteSpace: "nowrap" }}>Lastet opp</Table.Head>
                            <Table.Head style={{ whiteSpace: "nowrap" }}>Sist oppdatert</Table.Head>
                            <Table.Head></Table.Head>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {visibleDocuments.length === 0 ? (
                            <Table.Row>
                                <Table.Cell colSpan={10}>
                                    <Text>Ingen dokumenter lastet opp ennå</Text>
                                </Table.Cell>
                            </Table.Row>
                        ) : (
                            visibleDocuments.map((d) => (
                                <Table.Row key={d.id}>
                                    <Table.Cell>{d.title}</Table.Cell>
                                    <Table.Cell>{d.ingress}</Table.Cell>
                                    <Table.Cell>{DOCUMENT_TYPE_LABELS[d.documentType]}</Table.Cell>
                                    <Table.Cell>{CHAIN_LABELS[d.chain ?? "EXTERNAL_RETAIL"]}</Table.Cell>
                                    <Table.Cell>{d.fileName}</Table.Cell>
                                    <Table.Cell>{formatDate(d.startDate)}</Table.Cell>
                                    <Table.Cell>{formatDate(d.endDate)}</Table.Cell>
                                    <Table.Cell>{d.uploadedBy}</Table.Cell>
                                    <Table.Cell>{formatDate(d.uploadedAt)}</Table.Cell>
                                    <Table.Cell>{formatDate(d.updatedAt)}</Table.Cell>
                                    <Table.Cell>
                                        <Flex gap="200">
                                            <Button variant="secondary" onClick={() => handleEdit(d)}>
                                                Rediger
                                            </Button>
                                            <Button variant="secondary" onClick={() => handleDelete(d.id)}>
                                                Slett
                                            </Button>
                                        </Flex>
                                    </Table.Cell>
                                </Table.Row>
                            ))
                        )}
                    </Table.Body>
                </Table>
            )}
        </div>
    );
};

export default DocumentUpload;
